/**
 * Builds pagination links based on the total number of results and results per page.
 */

const Pagination = ({
    numResults,
    resultsPerPage,
    currentPage,
    onPageChange,
}) => {
    let num_of_pages = Math.ceil(numResults / resultsPerPage);

    if (numResults > resultsPerPage) {
        const curr_path = window.location.pathname;

        return (
            <nav
                aria-label="Job Listing Page Navigation"
                className="job_results__pagination"
            >
                {/* Previous Button */}
                <button
                    type="button"
                    title="Previous Page"
                    className={`btn-prev ${
                        currentPage === 1 ? "disabled" : ""
                    }`}
                    onClick={(e) =>
                        currentPage !== 1 && onPageChange(e, currentPage - 1)
                    }
                    {...(currentPage === 1 ? { disabled: true } : {})} // Conditionally add 'disabled' attribute
                    aria-disabled={currentPage === 1}
                >
                    « Previous
                </button>

                {/* Page Numbers */}
                <ul className="pagination">
                    {num_of_pages > 5 ? (
                        <>
                            {/* First Page and Ellipsis */}
                            {currentPage > 2 && (
                                <li key={1}>
                                    <a
                                        href={curr_path}
                                        onClick={(e) => onPageChange(e, 1)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault(); // Prevent spacebar from scrolling
                                                onPageChange(e, 1);
                                            }
                                        }}
                                    >
                                        1
                                    </a>
                                </li>
                            )}

                            {currentPage > 3 && (
                                <li className="disabled" aria-hidden="true">
                                    <span>&hellip;</span>
                                </li>
                            )}

                            {/* Previous Page */}
                            {currentPage > 1 && (
                                <li key={currentPage - 1}>
                                    <a
                                        rel="prev"
                                        href={`${curr_path}${
                                            currentPage - 1 !== 1
                                                ? `?pg=${currentPage - 1}`
                                                : ""
                                        }`}
                                        onClick={(e) =>
                                            onPageChange(e, currentPage - 1)
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            ) {
                                                e.preventDefault(); // Prevent spacebar from scrolling
                                                onPageChange(
                                                    e,
                                                    currentPage - 1
                                                );
                                            }
                                        }}
                                        tabIndex="0" // Ensures the link is focusable
                                    >
                                        {currentPage - 1}
                                    </a>
                                </li>
                            )}

                            {/* Current Page */}
                            <li
                                className="active disabled"
                                aria-current="page"
                                key={currentPage}
                            >
                                <span>{currentPage}</span>
                            </li>

                            {/* Next Pages */}
                            {currentPage < num_of_pages && (
                                <li key={currentPage + 1}>
                                    <a
                                        rel="next"
                                        href={`${curr_path}?pg=${
                                            currentPage + 1
                                        }`}
                                        onClick={(e) =>
                                            onPageChange(e, currentPage + 1)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault(); // Prevent spacebar from scrolling
                                                onPageChange(e, currentPage + 1);
                                            }
                                        }}
                                    >
                                        {currentPage + 1}
                                    </a>
                                </li>
                            )}

                            {/* Two Pages Ahead */}
                            {currentPage === 1 && (
                                <li key={currentPage + 2}>
                                    <a
                                        href={`${curr_path}?pg=${
                                            currentPage + 2
                                        }`}
                                        onClick={(e) =>
                                            onPageChange(e, currentPage + 2)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault(); // Prevent spacebar from scrolling
                                                onPageChange(e, currentPage + 2);
                                            }
                                        }}
                                        
                                    >
                                        {currentPage + 2}
                                    </a>
                                </li>
                            )}

                            {/* Last Page and Ellipsis */}
                            {currentPage < num_of_pages - 1 && (
                                <>
                                    {currentPage < num_of_pages - 2 && (
                                        <li
                                            className="disabled"
                                            aria-hidden="true"
                                        >
                                            <span>&hellip;</span>
                                        </li>
                                    )}
                                    <li key={num_of_pages}>
                                        <a
                                            href={`${curr_path}?pg=${num_of_pages}`}
                                            onClick={(e) =>
                                                onPageChange(e, num_of_pages)
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault(); // Prevent spacebar from scrolling
                                                    onPageChange(e, num_of_pages);
                                                }
                                            }}
                                        >
                                            {num_of_pages}
                                        </a>
                                    </li>
                                </>
                            )}
                        </>
                    ) : (
                        // Fewer than 5 pages
                        Array.from({ length: num_of_pages }, (_, i) => (
                            <li
                                key={i + 1}
                                className={
                                    currentPage === i + 1
                                        ? "active disabled"
                                        : ""
                                }
                            >
                                <a
                                    href={`${curr_path}?pg=${i + 1}`}
                                    onClick={(e) => onPageChange(e, i + 1)}
                                >
                                    {i + 1}
                                </a>
                            </li>
                        ))
                    )}
                </ul>

                {/* Next Button */}
                <button
                    type="button"
                    title="Next Page"
                    className={`btn-next ${currentPage >= num_of_pages ? "disabled" : ""}`}
                    onClick={(e) =>
                        currentPage < num_of_pages &&
                        onPageChange(e, currentPage + 1)
                    }
                    onKeyDown={(e) => {
                        if (
                            (e.key === "Enter" || e.key === " ") &&
                            currentPage < num_of_pages
                        ) {
                            e.preventDefault(); // Prevent spacebar from scrolling
                            onPageChange(e, currentPage + 1);
                        }
                    }}
                    {...(currentPage >= num_of_pages ? { disabled: true } : {})}
                    aria-disabled={currentPage >= num_of_pages}
                >
                    Next »
                </button>
            </nav>
        );
    } else {
        return <div className="job_results__pagination"></div>;
    }
};

export default Pagination;
